import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '@/config/site.config';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import FlagIcon from '@/components/FlagIcon';

const LANGUAGE_CODES: Record<SupportedLanguage, string> = {
  en: 'EN',
  ua: 'UA',
  ru: 'RU',
  es: 'ES',
  pt: 'PT',
  fr: 'FR',
  de: 'DE',
};

const LanguageSwitcher: React.FC = () => {
  const { language } = useLanguage();
  const location = useLocation();

  const handleLanguageChange = (newLang: SupportedLanguage) => {
    if (newLang === language) return;
    
    // Get current path without language prefix
    let currentPath = location.pathname;
    for (const lang of SUPPORTED_LANGUAGES) {
      if (currentPath.startsWith(`/${lang}/`)) {
        currentPath = currentPath.slice(lang.length + 1);
        break;
      } else if (currentPath === `/${lang}`) {
        currentPath = '/';
        break;
      }
    }
    
    // Build new URL for physical file navigation
    let newPath: string;
    if (newLang === 'en') {
      newPath = currentPath || '/';
    } else {
      newPath = currentPath === '/' ? `/${newLang}/` : `/${newLang}${currentPath}`;
    }
    
    // Use window.location for full page reload to physical file
    window.location.href = newPath;
  };

  return (
    <Select value={language} onValueChange={(value: SupportedLanguage) => handleLanguageChange(value)}>
      <SelectTrigger className="w-auto gap-1.5 h-9 px-2.5 bg-background border-border" aria-label="Select language">
        <SelectValue>
          <span className="flex items-center gap-1.5">
            <FlagIcon language={language} />
            <span className="text-sm font-medium">{LANGUAGE_CODES[language]}</span>
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-popover border-border min-w-0">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <SelectItem key={lang} value={lang} className="cursor-pointer">
            <span className="flex items-center gap-1.5">
              <FlagIcon language={lang} />
              <span className="text-sm font-medium">{LANGUAGE_CODES[lang]}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSwitcher;
